/**
 * Review Card Component
 * Displays a single tool review
 */

import { formatDistanceToNow } from "date-fns";
import { User } from "lucide-react";
import { StarRating } from "./StarRating";
import { cn } from "@/utils/cn";
import type { ToolReview, MyReview } from "@/types/tools";

interface ReviewCardProps {
  /** The review to display */
  review: ToolReview | MyReview;
  /** Whether this is the current user's review */
  isOwn?: boolean;
  /** Callback when edit is clicked */
  onEdit?: () => void;
  /** Callback when delete is clicked */
  onDelete?: () => void;
  /** Additional className */
  className?: string;
}

export function ReviewCard({
  review,
  isOwn = false,
  onEdit,
  onDelete,
  className,
}: ReviewCardProps) {
  const isPending = review.status === "pending";
  const isRejected = review.status === "rejected";
  const rejectionReason =
    "rejectionReason" in review ? review.rejectionReason : undefined;

  return (
    <div
      className={cn(
        "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4",
        isPending &&
          "border-yellow-300 dark:border-yellow-600 bg-yellow-50/50 dark:bg-yellow-900/10",
        isRejected &&
          "border-red-300 dark:border-red-600 bg-red-50/50 dark:bg-red-900/10",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {review.userName || "Anonymous"}
              </span>
              {isOwn && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  You
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <StarRating value={review.rating} size="sm" />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatDistanceToNow(new Date(review.createdAt), {
                  addSuffix: true,
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Status badge for own review */}
        {isOwn && (isPending || isRejected) && (
          <div
            className={cn(
              "text-xs px-2 py-1 rounded-full font-medium",
              isPending &&
                "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300",
              isRejected &&
                "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
            )}
          >
            {isPending ? "Pending Approval" : "Rejected"}
          </div>
        )}
      </div>

      {/* Review content */}
      <div className="mt-3">
        {review.reviewTitle && (
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
            {review.reviewTitle}
          </h4>
        )}
        {review.reviewText && (
          <p className="text-gray-600 dark:text-gray-300 text-sm whitespace-pre-wrap">
            {review.reviewText}
          </p>
        )}
      </div>

      {/* Rejection reason */}
      {isOwn && isRejected && rejectionReason && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300">
            <span className="font-medium">Reason:</span> {rejectionReason}
          </p>
        </div>
      )}

      {/* Actions for own review */}
      {isOwn && (onEdit || onDelete) && (
        <div className="mt-4 flex items-center gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          {onEdit && (
            <button
              onClick={onEdit}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
            >
              Edit Review
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium"
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
